"use client";

import Link from "next/link";
import { Fragment, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Routes } from "@/shared/constants";

/**
 * Structured legal text stored in locale files (`legalPages.*`).
 *
 * Paragraph entries are plain strings or one of: `{ ul }`, `{ ol }`, `{ table }`.
 * Inline markup inside strings: `**bold**` and `[label](url)`;
 * `route:NAME` urls resolve through `Routes` (e.g. `route:POLICY`).
 */
export type LegalBlock =
    | string
    | { ul: string[] }
    | { ol: string[] }
    | { table: { head: string[]; rows: string[][] } };

export type LegalSection = { title: string; paragraphs: LegalBlock[] };

export type LegalDoc = { title: string; lead?: string; sections: LegalSection[] };

const INLINE_TOKEN = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;

const resolveHref = (href: string): string => {
    if (href.startsWith("route:")) {
        const name = href.slice("route:".length) as keyof typeof Routes;
        return (Routes[name] as string | undefined) ?? "/";
    }
    return href;
};

export function renderLegalInline(text: string): ReactNode[] {
    const nodes: ReactNode[] = [];
    let last = 0;
    let key = 0;
    for (const match of text.matchAll(INLINE_TOKEN)) {
        const index = match.index ?? 0;
        if (index > last) nodes.push(text.slice(last, index));
        if (match[1] !== undefined) {
            nodes.push(<strong key={key++}>{match[1]}</strong>);
        } else {
            const href = resolveHref(match[3]);
            if (match[3].startsWith("route:")) {
                nodes.push(
                    <Link key={key++} href={href}>
                        {match[2]}
                    </Link>,
                );
            } else if (/^https:\/\/t\.me\//.test(href)) {
                nodes.push(
                    <a key={key++} href={href} target="_blank" rel="noopener noreferrer">
                        {match[2]}
                    </a>,
                );
            } else {
                nodes.push(
                    <a key={key++} href={href}>
                        {match[2]}
                    </a>,
                );
            }
        }
        last = index + match[0].length;
    }
    if (last < text.length) nodes.push(text.slice(last));
    return nodes;
}

type LegalBlocksProps = {
    blocks: LegalBlock[];
    /** `span` mirrors the legacy policy/terms markup (span + br); `p` is used for card-style pages. */
    paragraphTag?: "span" | "p";
    tableClassName?: string;
};

export const LegalBlocks = ({ blocks, paragraphTag = "p", tableClassName }: LegalBlocksProps) => {
    const flat = paragraphTag === "span";

    return (
        <>
            {blocks.map((block, index) => {
                if (typeof block === "string") {
                    return (
                        <Fragment key={index}>
                            {flat ? <span>{renderLegalInline(block)}</span> : <p>{renderLegalInline(block)}</p>}
                            {flat ? <br /> : null}
                        </Fragment>
                    );
                }
                if ("ul" in block || "ol" in block) {
                    const ordered = "ol" in block;
                    const items = ordered ? block.ol : block.ul;
                    const List = ordered ? "ol" : "ul";
                    return (
                        <Fragment key={index}>
                            <List>
                                {items.map((item, itemIndex) => (
                                    <li key={itemIndex}>{renderLegalInline(item)}</li>
                                ))}
                            </List>
                            {flat ? <br /> : null}
                        </Fragment>
                    );
                }
                return (
                    <Fragment key={index}>
                        <table className={tableClassName}>
                            <thead>
                                <tr>
                                    {block.table.head.map((cell, cellIndex) => (
                                        <th key={cellIndex}>{renderLegalInline(cell)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {block.table.rows.map((row, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {row.map((cell, cellIndex) => (
                                            <td key={cellIndex}>{renderLegalInline(cell)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {flat ? <br /> : null}
                    </Fragment>
                );
            })}
        </>
    );
};

/** Reads a structured legal document (`legalPages.privacy` | `legalPages.terms` | `legalPages.payment`) in the active language. */
export const useLegalDoc = (name: "privacy" | "terms" | "payment"): LegalDoc | null => {
    const { t } = useTranslation("common");
    const doc = t(`legalPages.${name}`, { returnObjects: true }) as unknown;

    if (!doc || typeof doc !== "object" || !Array.isArray((doc as LegalDoc).sections)) return null;

    return doc as LegalDoc;
};
